import React, { useState, useEffect } from 'react';
import { doc, updateDoc, getDoc } from 'firebase/firestore';
import { db } from './firebase';
import './OverrideFees.css';

const roundMoney = (amount) => Math.round(amount * 100) / 100;

function OverrideFees({ student, onClose }) {
  const [schoolData, setSchoolData] = useState(null);
  const [termStructure, setTermStructure] = useState(null);
  const [loading, setLoading] = useState(true);
  
  const [formData, setFormData] = useState({
    overrideType: 'term', // 'term', 'admin', or 'both'
    termNumber: '',
    lessonsRemaining: '',
    calculatedTermFee: 0,
    customTermFee: '',
    customAdminFee: '',
    reason: ''
  });

  const [adminFeeStructure, setAdminFeeStructure] = useState(null);

  useEffect(() => {
    loadSchoolData();
    loadAdminFeeStructure();
  }, []);

  useEffect(() => {
    if (formData.lessonsRemaining && schoolData) {
      calculateProRataFee();
    }
  }, [formData.lessonsRemaining, formData.termNumber, schoolData]);

  const loadSchoolData = async () => {
    try {
      if (student.schoolRef) {
        const schoolDoc = await getDoc(student.schoolRef);
        if (schoolDoc.exists()) {
          const school = { id: schoolDoc.id, ...schoolDoc.data() };
          setSchoolData(school);

          if (school.termStructureRef) {
            const termStructureDoc = await getDoc(school.termStructureRef);
            if (termStructureDoc.exists()) {
              setTermStructure(termStructureDoc.data());
            }
          }
        }
      }
    } catch (error) {
      console.error('Error loading school data:', error);
    } finally {
      setLoading(false);
    }
  };

  const loadAdminFeeStructure = async () => {
    try {
      const adminFeeDoc = await getDoc(doc(db, 'adminFeeStructure', 'current'));
      
      if (adminFeeDoc.exists()) {
        setAdminFeeStructure(adminFeeDoc.data());
      } else {
        console.log('No admin fee structure found - using defaults');
        setAdminFeeStructure({
          defaultAdminFee: 160,
          brandBuildingFund: 20,
          franchiseeKeeps: 20,
          headOfficeGets: 120,
          includesTshirt: true,
          waiverRules: {
            zero: { bb: 0, franchisee: 0, ho: 0, tshirt: false },
            partial40: { bb: 20, franchisee: 20, ho: 0, tshirt: false }
          }
        });
      }
    } catch (error) {
      console.error('Error loading admin fee structure:', error);
    }
  };

  const calculateAdminFeeBreakdown = (adminFeeAmount) => {
    if (!adminFeeStructure) {
      return null;
    }

    const amount = roundMoney(parseFloat(adminFeeAmount));

    // Case 1: No admin fee (R0)
    if (amount === 0) {
      return {
        total: 0,
        bbContribution: adminFeeStructure.waiverRules?.zero?.bb || 0,
        franchiseeKeeps: adminFeeStructure.waiverRules?.zero?.franchisee || 0,
        headOfficeGets: adminFeeStructure.waiverRules?.zero?.ho || 0,
        tshirtIncluded: adminFeeStructure.waiverRules?.zero?.tshirt || false
      };
    }

    // Case 2: Partial fee (R40)
    if (amount === 40) {
      return {
        total: amount,
        bbContribution: adminFeeStructure.waiverRules?.partial40?.bb || 20,
        franchiseeKeeps: adminFeeStructure.waiverRules?.partial40?.franchisee || 20,
        headOfficeGets: adminFeeStructure.waiverRules?.partial40?.ho || 0,
        tshirtIncluded: adminFeeStructure.waiverRules?.partial40?.tshirt || false
      };
    }

    // Case 3: Standard fee (R160 or R200 - defaultAdminFee)
    if (amount === adminFeeStructure.defaultAdminFee || amount === 160 || amount === 200) {
      return {
        total: amount,
        bbContribution: adminFeeStructure.brandBuildingFund || 20,
        franchiseeKeeps: adminFeeStructure.franchiseeKeeps || 20,
        headOfficeGets: adminFeeStructure.headOfficeGets || 120,
        tshirtIncluded: adminFeeStructure.includesTshirt !== undefined ? adminFeeStructure.includesTshirt : true
      };
    }

    // Case 4: Custom amount
    // R20 to Brand Building, remainder to Franchisee
    return {
      total: amount,
      bbContribution: 20,
      franchiseeKeeps: roundMoney(amount - 20),
      headOfficeGets: 0,
      tshirtIncluded: false
    };
  };

  // Calculate which term fees are applicable based on student start date and today's date
  const calculateApplicableTermFees = (studentStartDate, standardTermFee, feeOverrides) => {
    if (!termStructure || !termStructure.termDates) {
      console.log('No term structure available');
      return 0;
    }

    const startDate = studentStartDate.toDate ? studentStartDate.toDate() : new Date(studentStartDate);
    startDate.setHours(0, 0, 0, 0);

    const today = new Date();
    today.setHours(0, 0, 0, 0);
    const currentYear = today.getFullYear();

    let totalTermFees = 0;

    // Check each term (up to 4)
    for (let termNum = 1; termNum <= 4; termNum++) {
      const termData = termStructure.termDates[`term${termNum}`];
      if (!termData || !termData.start || !termData.end) continue;

      const [startMonth, startDay] = termData.start.split('-').map(Number);
      const [endMonth, endDay] = termData.end.split('-').map(Number);
      
      const termStart = new Date(currentYear, startMonth - 1, startDay);
      termStart.setHours(0, 0, 0, 0);
      
      const termEnd = new Date(currentYear, endMonth - 1, endDay);
      termEnd.setHours(23, 59, 59, 999);

      // Student should be charged if:
      // 1. They started before the term ended
      // 2. Today's date is past the term start (term has begun)
      if (startDate <= termEnd && today >= termStart) {
        // Check if this term has an override
        const termOverride = feeOverrides.find(
          o => o.termNumber === termNum && (o.overrideType === 'term' || o.overrideType === 'both')
        );
        
        if (termOverride && termOverride.termFee !== undefined) {
          totalTermFees = roundMoney(totalTermFees + termOverride.termFee);
        } else {
          totalTermFees = roundMoney(totalTermFees + standardTermFee);
        }
      }
    }

    return totalTermFees;
  };

  const calculateProRataFee = () => {
    if (!schoolData || !formData.termNumber || !formData.lessonsRemaining) return;

    const termFee = student.feeStructure?.standardFee || 0;
    const lessonsPerTerm = schoolData.lessonsPerTerm || 8;
    const lessonsRemaining = parseInt(formData.lessonsRemaining) || 0;

    const calculatedFee = roundMoney((termFee / lessonsPerTerm) * lessonsRemaining);

    setFormData(prev => ({
      ...prev,
      calculatedTermFee: calculatedFee,
      customTermFee: calculatedFee.toFixed(2)
    }));
  };

  const handleChange = (e) => {
    const { name, value } = e.target;
    setFormData(prev => ({
      ...prev,
      [name]: value
    }));
  };

  const handleSubmit = async (e) => {
    e.preventDefault();

    // Validation: Term number only required for term fee overrides
    if (formData.overrideType === 'term' || formData.overrideType === 'both') {
      if (!formData.termNumber) {
        alert('Please select a term for the term fee override');
        return;
      }
      if (!formData.customTermFee) {
        alert('Please enter a term fee amount');
        return;
      }
    }

    if (formData.overrideType === 'admin' || formData.overrideType === 'both') {
      if (!formData.customAdminFee && formData.customAdminFee !== '0') {
        alert('Please enter an admin fee amount');
        return;
      }
    }

    try {
      // Verify student ID exists
      if (!student || !student.id) {
        console.error('Student ID missing:', student);
        alert('Error: Student ID is missing. Please refresh and try again.');
        return;
      }

      console.log('Saving override for student:', student.id);
      console.log('Override type:', formData.overrideType);

      const overrideData = {
        overrideType: formData.overrideType,
        reason: formData.reason || 'Fee adjustment',
        appliedDate: new Date()
      };

      // For term and "both" overrides, include term-specific data
      if (formData.overrideType === 'term' || formData.overrideType === 'both') {
        overrideData.termNumber = parseInt(formData.termNumber);
        overrideData.termFee = roundMoney(parseFloat(formData.customTermFee));
        overrideData.lessonsRemaining = formData.lessonsRemaining ? parseInt(formData.lessonsRemaining) : null;
      }

      // For admin overrides, include admin fee
      if (formData.overrideType === 'admin' || formData.overrideType === 'both') {
        overrideData.adminFee = roundMoney(parseFloat(formData.customAdminFee));
      }

      // Store override in student record
      const studentRef = doc(db, 'students', student.id);
      
      // Get existing overrides
      const studentDoc = await getDoc(studentRef);
      if (!studentDoc.exists()) {
        alert('Error: Student record not found.');
        return;
      }

      const currentData = studentDoc.data();
      const existingOverrides = currentData.feeOverrides || [];
      console.log('Existing overrides:', existingOverrides);

      let filteredOverrides;
      
      if (formData.overrideType === 'admin') {
        // For admin-only: remove any existing admin-only overrides
        filteredOverrides = existingOverrides.filter(
          o => o.overrideType !== 'admin'
        );
      } else if (formData.overrideType === 'term' || formData.overrideType === 'both') {
        // For term overrides: remove existing override for this specific term
        filteredOverrides = existingOverrides.filter(
          o => o.termNumber !== parseInt(formData.termNumber)
        );
      }

      // Add new override
      filteredOverrides.push(overrideData);
      console.log('Updated overrides array:', filteredOverrides);

      // Prepare update object
      const updateData = {
        feeOverrides: filteredOverrides,
        updatedAt: new Date()
      };

      // If admin fee override, recalculate breakdown and update related fields
      if (formData.overrideType === 'admin' || formData.overrideType === 'both') {
        const newAdminFee = roundMoney(parseFloat(formData.customAdminFee));
        const breakdown = calculateAdminFeeBreakdown(newAdminFee);
        
        if (breakdown) {
          console.log('Calculated new breakdown:', breakdown);
          updateData.adminFeeBreakdown = breakdown;
          updateData.adminFee = newAdminFee;
        }
      }

      // Recalculate totalExpected and balance for ANY override type
      if (formData.overrideType === 'admin' || formData.overrideType === 'term' || formData.overrideType === 'both') {
        // Get the admin fee (with override if applicable)
        let adminFeeAmount;
        if (formData.overrideType === 'admin' || formData.overrideType === 'both') {
          adminFeeAmount = roundMoney(parseFloat(formData.customAdminFee));
        } else {
          // Check if there's an existing admin override
          const existingAdminOverride = filteredOverrides.find(
            o => o.overrideType === 'admin' || o.overrideType === 'both'
          );
          adminFeeAmount = existingAdminOverride?.adminFee ?? (currentData.adminFee || 0);
        }

        // Calculate applicable term fees using the same logic as AddStudent
        const standardTermFee = currentData.feeStructure?.standardFee || 0;
        const totalTermFees = calculateApplicableTermFees(
          currentData.startDate,
          standardTermFee,
          filteredOverrides
        );
        
        updateData.totalExpected = roundMoney(adminFeeAmount + totalTermFees);
        
        // Calculate balance (negative = owing)
        const totalPaid = currentData.totalPaid || 0;
        updateData.balance = roundMoney(totalPaid - updateData.totalExpected);
        
        console.log('Recalculated financial summary:');
        console.log('  - Admin fee:', adminFeeAmount);
        console.log('  - Applicable term fees:', totalTermFees);
        console.log('  - Total expected:', updateData.totalExpected);
        console.log('  - Total paid:', totalPaid);
        console.log('  - Balance:', updateData.balance);
      }

      await updateDoc(studentRef, updateData);

      console.log('Override saved successfully!');
      alert('Fee override saved successfully!');
      onClose();
    } catch (error) {
      console.error('Error saving override:', error);
      console.error('Error details:', error.message);
      alert(`Error saving override: ${error.message}`);
    }
  };

  if (loading) {
    return (
      <div className="modal-overlay">
        <div className="modal-content">
          <p>Loading...</p>
        </div>
      </div>
    );
  }

  return (
    <div className="modal-overlay">
      <div className="modal-content override-modal">
        <div className="modal-header">
          <h2>{'\u2699\uFE0F'} Override Fees - {student.studentName}</h2>
          <button onClick={onClose} className="btn-close">X</button>
        </div>

        <div className="student-info-box">
          <p><strong>School:</strong> {student.schoolName}</p>
          <p><strong>Standard Term Fee:</strong> R{student.feeStructure?.standardFee || 0}</p>
          <p><strong>Lessons per Term:</strong> {schoolData?.lessonsPerTerm || 8}</p>
          <p><strong>Admin Fee:</strong> R{student.adminFee || 0}</p>
        </div>

        <form onSubmit={handleSubmit}>
          <div className="form-group">
            <label>What do you want to override? *</label>
            <select
              name="overrideType"
              value={formData.overrideType}
              onChange={handleChange}
              required
            >
              <option value="term">Term Fee Only</option>
              <option value="admin">Admin Fee Only</option>
              <option value="both">Both Term Fee & Admin Fee</option>
            </select>
          </div>

          {/* Term selection only required for term fee overrides */}
          {(formData.overrideType === 'term' || formData.overrideType === 'both') && (
            <div className="form-group">
              <label>Which Term? *</label>
              <select
                name="termNumber"
                value={formData.termNumber}
                onChange={handleChange}
                required
              >
                <option value="">Select Term</option>
                <option value="1">Term 1</option>
                <option value="2">Term 2</option>
                <option value="3">Term 3</option>
                <option value="4">Term 4</option>
              </select>
            </div>
          )}

          {(formData.overrideType === 'term' || formData.overrideType === 'both') && (
            <>
              <div className="form-group">
                <label>Lessons Remaining in Term</label>
                <input
                  type="number"
                  name="lessonsRemaining"
                  value={formData.lessonsRemaining}
                  onChange={handleChange}
                  min="1"
                  max={schoolData?.lessonsPerTerm || 8}
                  placeholder={`1-${schoolData?.lessonsPerTerm || 8}`}
                />
                <small>Optional: Enter to auto-calculate pro-rata fee</small>
              </div>

              {formData.calculatedTermFee > 0 && (
                <div className="calculation-preview">
                  <p><strong>{'\uD83D\uDCA1'} Calculated Pro-Rata:</strong></p>
                  <p className="calculation-formula">
                    R{student.feeStructure?.standardFee || 0} {'\u00F7'} {schoolData?.lessonsPerTerm || 8} lessons {'\u00D7'} {formData.lessonsRemaining} remaining
                    = <strong>R{formData.calculatedTermFee.toFixed(2)}</strong>
                  </p>
                </div>
              )}

              <div className="form-group">
                <label>Custom Term Fee Amount (R) *</label>
                <input
                  type="number"
                  name="customTermFee"
                  value={formData.customTermFee}
                  onChange={handleChange}
                  step="0.01"
                  min="0"
                  required
                  placeholder="Enter amount"
                />
                <small>You can edit the calculated amount above</small>
              </div>
            </>
          )}

          {(formData.overrideType === 'admin' || formData.overrideType === 'both') && (
            <div className="form-group">
              <label>Custom Admin Fee Amount (R) *</label>
              <input
                type="number"
                name="customAdminFee"
                value={formData.customAdminFee}
                onChange={handleChange}
                step="0.01"
                min="0"
                required
                placeholder="Enter amount (0 for no admin fee)"
              />
              <small>
                {formData.overrideType === 'admin' 
                  ? 'Admin fee is a one-time charge. Enter 0 if no admin fee should be charged.'
                  : 'Enter 0 if no admin fee should be charged'
                }
              </small>
            </div>
          )}

          <div className="form-group">
            <label>Reason for Override</label>
            <textarea
              name="reason"
              value={formData.reason}
              onChange={handleChange}
              rows="3"
              placeholder="e.g., Joined mid-term, attended 4 out of 8 lessons"
            />
          </div>

          <div className="button-group">
            <button type="button" onClick={onClose} className="btn-secondary">
              Cancel
            </button>
            <button type="submit" className="btn-primary">
              {'\uD83D\uDCBE'} Save Override
            </button>
          </div>
        </form>
      </div>
    </div>
  );
}

export default OverrideFees;